import { useState, type FormEvent } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { Button, ErrorBanner, Field, Input, PageHeader, Panel, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { requireSupabase } from "../lib/supabase";

function strongEnough(password: string) {
  const categories = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(password)).length;
  return password.length >= 12 && categories >= 3;
}

export function AccountPage() {
  const { cloudConfigured, profile } = useAuth();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const role = profile?.role === "admin" ? "Administrator" : profile?.role === "dispatcher" ? "Dispo" : "Lager";

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!strongEnough(password)) return setError("Das neue Passwort braucht mindestens 12 Zeichen und drei Zeichenarten.");
    if (password !== confirmation) return setError("Die beiden Passwörter stimmen nicht überein.");
    setBusy(true);
    const { error: updateError } = await requireSupabase().auth.updateUser({ password });
    if (updateError) setError("Das Passwort konnte nicht geändert werden. Bitte erneut anmelden und noch einmal versuchen.");
    else {
      setPassword("");
      setConfirmation("");
      setSuccess("Das Passwort wurde geändert.");
    }
    setBusy(false);
  }

  return (
    <>
      <PageHeader title="Mein Konto" description="Persönliche Anmeldung und freigegebener Lagerzugriff." action={<StatusBadge tone={profile?.is_active ? "success" : "danger"}>{profile?.is_active ? "Freigeschaltet" : "Gesperrt"}</StatusBadge>} />
      {error && <ErrorBanner message={error} />}
      {success && <div className="success-banner"><ShieldCheck size={17} />{success}</div>}
      <section className="account-grid">
        <Panel title="Kontodaten"><dl className="account-details"><div><dt>Name</dt><dd>{profile?.full_name || "–"}</dd></div><div><dt>E-Mail</dt><dd>{profile?.email || "Lokale Vorschau"}</dd></div><div><dt>Rolle</dt><dd>{role}</dd></div><div><dt>Lagerzugriff</dt><dd>{profile?.warehouse?.name || "Beide Lager"}</dd></div></dl></Panel>
        <Panel title="Passwort ändern" description="Das Passwort wird direkt über Supabase Auth aktualisiert und nicht in den Lagerdaten gespeichert.">
          {cloudConfigured ? <form className="account-password-form" onSubmit={changePassword}><Field label="Neues Passwort" hint="Mindestens 12 Zeichen und drei Zeichenarten"><Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" required minLength={12} /></Field><Field label="Passwort wiederholen"><Input type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" required minLength={12} /></Field><Button type="submit" disabled={busy}><KeyRound size={16} />{busy ? "Wird geändert …" : "Passwort ändern"}</Button></form> : <div className="empty-state empty-state--compact"><strong>Im Vorschaumodus nicht verfügbar</strong><p>Die Funktion wird nach Verbindung der Testcloud aktiv.</p></div>}
        </Panel>
      </section>
    </>
  );
}
